import React, { useEffect, useState } from "react";
import { FaBuilding } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import styled from "styled-components";
import useApi from "../../hooks/useApi";
import { GET_ALL_BUSINESSES_URL, LOCK_TEST_BUSINESS_URL } from "../../api/urls";
import { updatePastquestionLock } from "../../store/slices/examSlice";
import SearchableDropdown from "../dropDown/SearchableDropdown";
import {
  ModalBackdrop,
  FormModalBox,
  FormModalIcon,
  ModalTitle,
  ModalBody,
  ModalActions,
  ModalCancelBtn,
  ModalSubmitBtn,
  ModalFormGroup,
  ModalFormLabel,
  ModalFormError,
} from "../../pages/Channels/style";

const VisibilityOption = styled.label`
  align-items: flex-start;
  background: #f7f9fd;
  border: 1px solid #dfe6f3;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  gap: 11px;
  margin-top: 18px;
  padding: 13px 14px;

  &:focus-within {
    border-color: #2f67d8;
    box-shadow: 0 0 0 3px rgba(47, 103, 216, 0.08);
  }
`;

const VisibilityCheckbox = styled.input`
  accent-color: #2f67d8;
  cursor: pointer;
  flex: 0 0 auto;
  height: 17px;
  margin: 2px 0 0;
  width: 17px;
`;

const VisibilityCopy = styled.span`
  color: #1f2a44;
  display: block;
  font: 13px/1.4 rebondG-Semi-Bold;

  small {
    color: #6b7a99;
    display: block;
    font: 12px/1.45 rebondBook;
    margin-top: 3px;
  }
`;

export const LockExamModal = ({
  examId,
  currentBusinessId,
  currentBusinessName,
  currentHideFromNonMembers = false,
  onClose,
}) => {
  const api = useApi();
  const dispatch = useDispatch();
  const [businesses, setBusinesses] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(
    currentBusinessId ? { id: currentBusinessId, orgName: currentBusinessName } : null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [hideFromNonMembers, setHideFromNonMembers] = useState(
    Boolean(currentHideFromNonMembers)
  );

  useEffect(() => {
    let cancelled = false;
    setIsFetching(true);
    api
      .get(GET_ALL_BUSINESSES_URL)
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.businesses || res.data || [];
        setBusinesses(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setFetchError(err?.response?.data?.error || "Could not load businesses");
      })
      .finally(() => !cancelled && setIsFetching(false));
    return () => { cancelled = true; };
  }, []);

  const handleSelect = (orgName, id) => {
    setError(null);
    const match = businesses.find((b) => b.id === id) || { id, orgName };
    setSelectedBusiness(match);
  };

  const submitLock = async (businessId, businessName) => {
    try {
      setIsSubmitting(true);
      setError(null);
      const restrictVisibility = Boolean(businessId && hideFromNonMembers);
      await api.post(`${LOCK_TEST_BUSINESS_URL}/${examId}`, {
        businessId,
        hideFromNonMembers: restrictVisibility,
      });
      dispatch(
        updatePastquestionLock({
          id: examId,
          isLocked: !!businessId,
          businessId,
          businessName: businessId ? businessName : null,
          hideFromNonMembers: restrictVisibility,
        })
      );
      toast.success(businessId ? "Test locked to business" : "Lock removed from test");
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not update lock. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = () => {
    if (!selectedBusiness?.id || isSubmitting) return;
    submitLock(selectedBusiness.id, selectedBusiness.orgName);
  };

  const handleUnlock = () => {
    if (isSubmitting) return;
    submitLock(null, null);
  };

  return (
    <ModalBackdrop onClick={onClose}>
      <FormModalBox onClick={(e) => e.stopPropagation()}>
        <FormModalIcon>
          <FaBuilding />
        </FormModalIcon>
        <ModalTitle>Lock Test to a Business</ModalTitle>
        <ModalBody>
          Only students registered under the selected business will be able to take this test.
        </ModalBody>

        {error && <ModalFormError>{error}</ModalFormError>}
        {fetchError && <ModalFormError>{fetchError}</ModalFormError>}

        <ModalFormGroup>
          <ModalFormLabel>Business</ModalFormLabel>
          {isFetching ? (
            <ModalBody style={{ margin: 0, textAlign: "left" }}>Loading businesses…</ModalBody>
          ) : (
            <SearchableDropdown
              options={businesses}
              label="orgName"
              id="id"
              finalVal="id"
              selectedVal={selectedBusiness?.orgName || ""}
              placeholder="Search for a business…"
              handleChange={handleSelect}
            />
          )}
        </ModalFormGroup>

        <VisibilityOption>
          <VisibilityCheckbox
            type="checkbox"
            checked={hideFromNonMembers}
            onChange={(event) => setHideFromNonMembers(event.target.checked)}
          />
          <VisibilityCopy>
            Hide from non-channel users
            <small>
              Only members of the selected business channel will see this test in their test list.
            </small>
          </VisibilityCopy>
        </VisibilityOption>

        <ModalActions style={{ marginTop: "24px" }}>
          <ModalCancelBtn type="button" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </ModalCancelBtn>
          <ModalSubmitBtn type="button" onClick={handleConfirm} disabled={!selectedBusiness?.id || isSubmitting}>
            {isSubmitting ? "Saving…" : "Lock Test"}
          </ModalSubmitBtn>
        </ModalActions>

        {currentBusinessId && (
          <ModalCancelBtn
            type="button"
            onClick={handleUnlock}
            disabled={isSubmitting}
            style={{ width: "100%", marginTop: "10px" }}
          >
            Remove Lock
          </ModalCancelBtn>
        )}
      </FormModalBox>
    </ModalBackdrop>
  );
};
